import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ModalActionsComponent, PasslyValidatedPassword, PasslyCheckbox, PasslyTextField } from '@app/components';
import { getModalSwitch, getLoaderBtn, handleResetUserPassword, resetUserPasswordResponseAction, handleGetUserData } from '@app/store/actions';

import { useTypedTranslation } from '@app/hooks';
import { AxiosResponse } from 'axios';
import {FormControl} from "@material-ui/core";

const validator = require('validator');

export interface IResetUserPasswordData {
    userId: string;
    userPasswordPolicy: any;
}

const initialState = {
    password: '',
    passwordConfirm: '',
    requirePassChange: true,
    formValid: false,
    errorMessage: ''
};

export default function ResetPasswordDialog(props: IResetUserPasswordData) {
    const dispatch = useDispatch();
    const { translation } = useTypedTranslation();

    const resetUserPasswordResponse = useSelector((state: any) => state.users.resetUserPasswordResponse);

    const [{ password, passwordConfirm, requirePassChange, formValid, errorMessage }, setFormState] = useState(initialState);

    const [passwordValidation, setPasswordValidation] = useState(false);
    const [passwordConfirmValidation, setPasswordConfirmValidation] = useState(false);

    const saveBtnPasswordValidation = !passwordValidation && !passwordConfirmValidation && formValid;

    useEffect(() => {
        if (resetUserPasswordResponse && resetUserPasswordResponse.status === 204) {
            dispatch(getModalSwitch(false));
            dispatch(getLoaderBtn(false));
            dispatch(resetUserPasswordResponseAction({} as AxiosResponse));
            dispatch(handleGetUserData(props.userId));
            setTimeout(() => setFormState(initialState), 250);
        } else if (resetUserPasswordResponse && resetUserPasswordResponse.response && resetUserPasswordResponse.response.status === 400) {
            dispatch(getLoaderBtn(false));
            setFormState((previousState) => ({ ...previousState, errorMessage: resetUserPasswordResponse.response.data.error.message }));
        } else {
            dispatch(getLoaderBtn(false));
        }
    }, [resetUserPasswordResponse]);

    useEffect(() => {
        if (password !== passwordConfirm || password === '') {
            setFormState((previousState) => ({ ...previousState, formValid: false }));
        } else {
            setFormState((previousState) => ({ ...previousState, formValid: true }));
        }
    }, [password, passwordConfirm]);

    const handlePassword = (e: any) => {
        let passwordValue = e.target.value;
        setFormState((previousState) => ({ ...previousState, password: passwordValue }));
        checkPasswordConfirmValid(passwordValue, passwordConfirm);
    };

    const handlePasswordConfirm = (e: any) => {
        onFormChange(e);
        checkPasswordConfirmValid(password, e.target.value);
    };

    const checkPasswordConfirmValid = (password: string, confirmPassword: string) => {
        if (!validator.isEmpty(confirmPassword) && password === confirmPassword) {
            setPasswordConfirmValidation(false);
        } else {
            setPasswordConfirmValidation(true);
        }
    };

    const onFormChange = (e: any) => {
        const { name, value } = e.target;
        setFormState((previousState) => ({ ...previousState, [name]: value }));
    };

    const handleRequirePasswordChange = (checked: boolean) => {
        setFormState((previousState) => ({ ...previousState, requirePassChange: checked }));
    };

    const handleSubmitBtn = () => {
        if (saveBtnPasswordValidation) {
            dispatch(handleResetUserPassword(props.userId, password, requirePassChange));
            dispatch(getLoaderBtn(true));
            setFormState((previousState) => ({ ...previousState, formValid: false }));
        } else {
            setFormState((previousState) => ({ ...previousState, errorMessage: translation('RESET_PASS_ERROR') }));
        }
    };

    const handleCancel = () => {
        dispatch(getModalSwitch(false));
        dispatch(getLoaderBtn(false));
        setTimeout(() => setFormState(initialState), 250);
        dispatch(resetUserPasswordResponseAction({} as AxiosResponse));
    };

    return (
        <>
            <form className='modal__form' method='post'>
                <div className='modal__body--row'>
                    <p>{translation('CHANGE_PASSWORD_BODY')}</p>
                </div>
                <FormControl>
                    <PasslyValidatedPassword value={password} onChange={handlePassword} />
                </FormControl>

                <div className='modal__body--row'>
                    <PasslyTextField
                        fullWidth
                        name='passwordConfirm'
                        type='password'
                        label={'CONFIRM_PASSWORD'}
                        value={passwordConfirm}
                        onChange={handlePasswordConfirm}
                        id='addUserPasswordConfirm-input'
                        error={passwordConfirmValidation}
                        helperText={passwordConfirmValidation ? translation('PASSWORDS_DONT_MATCH') : ''}
                        autoFocus
                    />
                </div>

                <div className='modal__body--row'>
                    <PasslyCheckbox
                        name='requirePassChange'
                        checked={requirePassChange}
                        onChange={handleRequirePasswordChange}
                        label={translation('EXPIRE_PASSWORD_HEADER')}
                    />
                </div>

                {errorMessage && (
                    <p>
                        <div className='modal__error'>{errorMessage}</div>
                    </p>
                )}
            </form>

            <ModalActionsComponent
                primaryBtn={translation('OK')}
                secondaryBtn={translation('CANCEL')}
                onClickPrimary={handleSubmitBtn}
                onClickSecondary={handleCancel}
                disabled={!saveBtnPasswordValidation}
                id='resetPassword'
            />
        </>
    );
}
